import { Link } from "react-router-dom";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyStore } from "./hooks/useMyStore";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";

function StatusBadge({ status }: { status: string }) {
  const label =
    status === "PUBLISHED"
      ? "Publicada"
      : status === "DISABLED"
        ? "Desativada"
        : "Rascunho";

  return (
    <span
      className={cn(
        "inline-block px-2 py-0.5 text-xs uppercase tracking-widest border",
        status === "PUBLISHED"
          ? "border-green-600 text-green-700 dark:text-green-400"
          : "border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300"
      )}
    >
      {label}
    </span>
  );
}

export default function DashboardPage() {
  const { user, loading: userLoading } = useCurrentUser();
  const { store, loading: storeLoading, publicPath, isPublished } = useMyStore();

  const loading = userLoading || storeLoading;

  return (
    <div className="container-elegant page-padding">
      <PageHeader title="Bem-vindo" />
      {loading ? (
        <div className="space-y-4 max-w-xl">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
          <p className="text-base font-light text-primary-600 dark:text-primary-400 mb-10">
            {user?.email}
          </p>

          {!store ? (
            <section className="max-w-xl border border-primary-200 dark:border-primary-800 panel-padding">
              <h2 className="text-xl font-light text-primary-900 dark:text-white mb-3">
                Configure sua loja
              </h2>
              <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
                Você ainda não criou uma loja. Comece definindo nome, slug e WhatsApp para
                atender seus clientes.
              </p>
              <Button asChild>
                <Link to="/store/settings">CRIAR MINHA LOJA</Link>
              </Button>
            </section>
          ) : (
            <section className="max-w-xl space-y-6">
              <div className="border border-primary-200 dark:border-primary-800 panel-padding">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <h2 className="text-2xl font-light text-primary-900 dark:text-white">
                    {store.name}
                  </h2>
                  <StatusBadge status={store.status} />
                </div>

                {store.status === "DRAFT" && (
                  <div className="mb-6">
                    <p className="text-xs font-light tracking-widest uppercase text-primary-600 dark:text-primary-400 mb-3">
                      Checklist de publicação
                    </p>
                    <ul className="text-sm font-light text-primary-700 dark:text-primary-300 space-y-2 list-disc list-inside">
                      <li>Nome e slug definidos</li>
                      <li
                        className={cn(
                          store.whatsappPhone ? "text-primary-700" : "text-amber-700 dark:text-amber-400"
                        )}
                      >
                        WhatsApp cadastrado
                        {!store.whatsappPhone && " (obrigatório para publicar)"}
                      </li>
                      <li>Publicar a loja quando estiver pronta</li>
                    </ul>
                    <Button asChild variant="outline" className="mt-6">
                      <Link to="/store/settings">CONTINUAR CONFIGURAÇÃO</Link>
                    </Button>
                  </div>
                )}

                {isPublished && publicPath && (
                  <div className="mb-4">
                    <p className="text-xs font-light tracking-widest uppercase text-primary-600 dark:text-primary-400 mb-2">
                      URL pública
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <code className="text-sm font-light text-primary-900 dark:text-white bg-primary-100 dark:bg-primary-800 px-3 py-2">
                        {publicPath}
                      </code>
                      <Button asChild variant="outline" size="sm">
                        <a href={publicPath} target="_blank" rel="noopener noreferrer">
                          ABRIR
                        </a>
                      </Button>
                    </div>
                  </div>
                )}

                {store.status !== "DRAFT" && !isPublished && (
                  <Button asChild variant="outline">
                    <Link to="/store/settings">EDITAR LOJA</Link>
                  </Button>
                )}
              </div>

              <div className="border border-primary-200 dark:border-primary-800 panel-padding">
                <h2 className="text-xl font-light text-primary-900 dark:text-white mb-3">
                  Catálogo
                </h2>
                <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
                  Organize categorias e cadastre produtos para sua vitrine.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button asChild variant="outline">
                    <Link to="/categories">CATEGORIAS</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/products/new">NOVO PRODUTO</Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/products">VER PRODUTOS</Link>
                  </Button>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
